import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check } from "lucide-react";
import avatar from "@/assets/varvara-portrait.webp";
import { StarField } from "@/components/landing/StarField";
import { CTAButton, ChannelLink, ChatCTAButton } from "@/components/landing/CTAButton";
import { LiveCounter } from "@/components/landing/LiveCounter";
import { lineContainer, MaskLine } from "@/lib/motion";
import { BENEFITS } from "@/lib/site";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const avatarY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section
      ref={ref}
      id="hero"
      className="relative overflow-hidden radial-purple pt-32 pb-20 sm:pt-40 sm:pb-28"
      data-testid="hero-section"
    >
      <StarField count={46} />
      <motion.div
        style={{ y: glowY }}
        className="pointer-events-none absolute left-1/2 top-0 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-[#5B3A78]/25 blur-[120px]"
      />

      <div className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 sm:px-8 lg:grid-cols-2 lg:gap-8">
        {/* Left — copy */}
        <div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#D4AF6E]/25 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-[#F0C674]"
          >
            Таро · Нумерология · Хиромантия
          </motion.p>

          <motion.h1
            variants={lineContainer}
            initial="hidden"
            animate="show"
            className="font-serif text-5xl font-bold leading-[1.05] text-[#EDE6D3] sm:text-6xl lg:text-7xl"
            data-testid="hero-title"
          >
            <MaskLine>Бабушка Варвара</MaskLine>
            <MaskLine className="text-gold-gradient">сделает</MaskLine>
            <MaskLine>персональный разбор</MaskLine>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-[#EDE6D3]/70 sm:text-lg"
          >
            Имя, дата рождения, Таро, ладонь или совместимость. Персональный разбор —
            прямо на сайте, без установки приложений. Или в Telegram и MAX — как удобнее.
          </motion.p>

          {/* Benefits */}
          <motion.ul
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-7 grid grid-cols-2 gap-3 sm:max-w-md"
            data-testid="hero-benefits"
          >
            {BENEFITS.map((b, i) => (
              <li
                key={b.label}
                className="chase-ring chase-ring--sequence relative flex items-center gap-2.5 rounded-xl border border-[#D4AF6E]/12 bg-[#111133]/50 px-3.5 py-2.5"
                style={{ "--i": i }}
              >
                <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full gold-gradient">
                  <Check className="h-3.5 w-3.5 text-[#0B0B1F]" strokeWidth={3} />
                </span>
                <span className="leading-tight">
                  <span className="block text-sm font-medium text-[#EDE6D3]">{b.label}</span>
                  <span className="block text-xs text-[#EDE6D3]/50">{b.note}</span>
                </span>
              </li>
            ))}
          </motion.ul>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <ChatCTAButton testId="hero-cta-chat">
              Начать разбор здесь
            </ChatCTAButton>
            <div className="flex flex-wrap items-center gap-3">
              <CTAButton
                goal="cta_telegram_bot"
                platform="telegram"
                primary={false}
                size="sm"
                showArrow={false}
                testId="hero-cta-bot-telegram"
              >
                В Telegram
              </CTAButton>
              <CTAButton
                goal="cta_max_bot"
                platform="max"
                primary={false}
                size="sm"
                showArrow={false}
                testId="hero-cta-bot"
              >
                В MAX
              </CTAButton>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="mt-4 flex flex-wrap items-center gap-4 text-sm text-[#EDE6D3]/55"
          >
            <span>Или подпишитесь на канал:</span>
            <ChannelLink platform="max" goal="cta_max_channel" testId="hero-cta-channel">
              Бабушка Варвара в MAX
            </ChannelLink>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.8 }}
            className="mt-8"
          >
            <LiveCounter />
          </motion.div>
        </div>

        {/* Right — avatar with parallax + shimmering cards */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="relative mx-auto w-full max-w-md lg:max-w-lg"
        >
          <motion.div style={{ y: avatarY }} className="relative">
            <div className="absolute -inset-6 rounded-full bg-gradient-to-b from-[#5B3A78]/30 to-[#D4AF6E]/15 blur-3xl" />
            <div
              className="relative"
              style={{
                // "closest-side" sizes the ellipse to reach exactly the box's
                // nearest edges, so opacity is guaranteed to hit 0 right at
                // the edge instead of stopping partway through the fade
                // (which, against the photo's warm tones, still read as a
                // visible seam even at 40-60% translucency).
                WebkitMaskImage:
                  "radial-gradient(closest-side at 50% 40%, #000 30%, transparent 100%)",
                maskImage:
                  "radial-gradient(closest-side at 50% 40%, #000 30%, transparent 100%)",
              }}
            >
              <img
                src={avatar}
                alt="Бабушка Варвара за столом с картами Таро и свечой"
                className="h-full w-full object-cover"
                loading="eager"
                width={848}
                height={1264}
              />
            </div>

            {/* Floating shimmering tarot cards */}
            {[
              { top: "6%", right: "-6%", rot: 12, delay: 0 },
              { bottom: "12%", left: "-7%", rot: -14, delay: 0.6 },
            ].map((c, i) => (
              <motion.div
                key={i}
                className="absolute hidden h-24 w-16 rounded-lg border border-[#F0C674]/60 bg-gradient-to-b from-[#1a1a44] to-[#111133] sm:block"
                style={{ ...c, rotate: c.rot }}
                animate={{ y: [0, -12, 0], boxShadow: [
                  "0 0 8px rgba(240,198,116,0.2)",
                  "0 0 26px rgba(240,198,116,0.6)",
                  "0 0 8px rgba(240,198,116,0.2)",
                ] }}
                transition={{ duration: 4, delay: c.delay, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className="flex h-full items-center justify-center">
                  <span className="font-serif text-2xl text-[#F0C674]">
                    {i === 0 ? "✦" : "☾"}
                  </span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
